// chartCoords.js — coordinate-conversion helpers for the Liquidity Map chart.
//
// Single source of truth for price↔pixel and idx↔pixel math used by the
// drawing engine (Fase B1). The existing 7 inline redeclarations inside
// `HeatmapCanvas.jsx` (lines 195-199, 450, 641, 672, 692, 793, 821, 2087)
// are LEFT UNCHANGED — they are tracked under P2 "Refactor HeatmapCanvas.jsx"
// and will be migrated to this module in a separate pass. Until then, this
// file is consumed ONLY by the new drawing engine.
//
// Canonical formulas (must match the inline definitions):
//   yOfPrice(price)  = chartH - ((price - min) / (max - min)) * chartH
//   priceOfY(y)      = max - (y / chartH) * (max - min)
//   xOfIdx(idx)      = idx * step + step / 2,  step = chartW / nCandles
//   idxOfX(x)        = (x - step / 2) / step
//
// All functions are PURE and side-effect free.

/**
 * Vertical mapping: price → pixel (top is high price, bottom is low price).
 *
 * @param {number} price      Y-domain value.
 * @param {{min:number, max:number}} viewRange  Currently visible price band.
 * @param {number} chartH     Height of the chart area in CSS pixels.
 * @returns {number}          Pixel Y coordinate within [0, chartH].
 */
export function yOfPrice(price, viewRange, chartH) {
  const { min, max } = viewRange;
  if (max <= min) return chartH;
  return chartH - ((price - min) / (max - min)) * chartH;
}

/**
 * Vertical mapping inverse: pixel → price.
 *
 * @param {number} y          Pixel Y within the chart area.
 * @param {{min:number, max:number}} viewRange
 * @param {number} chartH
 * @returns {number}          Price at that pixel.
 */
export function priceOfY(y, viewRange, chartH) {
  const { min, max } = viewRange;
  if (max <= min || chartH <= 0) return min;
  const ratio = 1 - y / chartH;
  return min + ratio * (max - min);
}

/**
 * Horizontal mapping: candle index → pixel (centred on each slot).
 * Mirrors the canonical formula used by the candle-pattern layer at
 * HeatmapCanvas line 2090-2092.
 *
 * @param {number} idx        Candle index (0-based, can be fractional for
 *                            sub-candle anchors).
 * @param {number} nCandles   Total visible candles in the brush range.
 * @param {number} chartW     Width of the chart area in CSS pixels.
 * @returns {number}          Pixel X.
 */
export function xOfIdx(idx, nCandles, chartW) {
  if (nCandles <= 0) return 0;
  const step = chartW / nCandles;
  return idx * step + step / 2;
}

/**
 * Horizontal mapping inverse: pixel → fractional candle index.
 *
 * @param {number} x          Pixel X within the chart area.
 * @param {number} nCandles
 * @param {number} chartW
 * @returns {number}          Fractional index (may be < 0 or > n-1 outside).
 */
export function idxOfX(x, nCandles, chartW) {
  if (nCandles <= 0 || chartW <= 0) return 0;
  const step = chartW / nCandles;
  return (x - step / 2) / step;
}

/**
 * Round an anchor to an integer index (clamped) and a finite price.
 * Drawings store anchors as {idx, price} but during draft creation the
 * pointer may yield fractional values — finalize() runs everything through
 * this normalizer.
 *
 * @param {{idx:number, price:number}} anchor
 * @param {number} nCandles
 * @returns {{idx:number, price:number}}
 */
export function normalizeAnchor(anchor, nCandles) {
  const idx = Math.max(0, Math.min(nCandles - 1, Math.round(anchor.idx)));
  const price = Number.isFinite(anchor.price) ? anchor.price : 0;
  return { idx, price };
}

/**
 * Build an anchor from a pixel position relative to the chart area.
 *
 * @param {number} px         Mouse X relative to chart area (NOT viewport).
 * @param {number} py         Mouse Y relative to chart area.
 * @param {object} ctx        { viewRange, chartW, chartH, nCandles }
 * @returns {{idx:number, price:number}}  Anchor in chart space, fractional.
 */
export function pointerToAnchor(px, py, ctx) {
  const { viewRange, chartW, chartH, nCandles } = ctx;
  return {
    idx: idxOfX(px, nCandles, chartW),
    price: priceOfY(py, viewRange, chartH),
  };
}

/**
 * Reverse of pointerToAnchor — anchor in chart space → pixel.
 *
 * @param {{idx:number, price:number}} anchor
 * @param {object} ctx        { viewRange, chartW, chartH, nCandles }
 * @returns {{x:number, y:number}}  Pixel coords within the chart area.
 */
export function anchorToPixel(anchor, ctx) {
  const { viewRange, chartW, chartH, nCandles } = ctx;
  return {
    x: xOfIdx(anchor.idx, nCandles, chartW),
    y: yOfPrice(anchor.price, viewRange, chartH),
  };
}
